<template>
  <select
    v-bind="selectBindings"
    :class="[
      'appearance-none rounded-[1px] border bg-no-repeat leading-none transition outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-70',
      sizeClass,
      toneClass,
      widthClass,
    ]"
    :style="selectStyle"
    @change="handleChange"
  >
    <slot />
  </select>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const model = defineModel<string | null>();

const props = withDefaults(
  defineProps<{
    size?: 'md' | 'sm' | 'toolbar';
    tone?: 'chat' | 'dark' | 'light';
    value?: string | null;
    width?: 'auto' | 'full' | 'toolbar';
  }>(),
  {
    size: 'md',
    tone: 'light',
    value: undefined,
    width: 'full',
  },
);

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-5.25 pl-1.5 pr-5.5 conference-text-control';
    case 'toolbar':
      return 'h-6.25 pl-2 pr-6 conference-text-helper';
    default:
      return 'h-6.25 pl-2 pr-6 conference-text-control';
  }
});
const widthClass = computed(() => {
  switch (props.width) {
    case 'auto':
      return 'w-auto';
    case 'toolbar':
      return 'w-50';
    default:
      return 'w-full';
  }
});

const resolvedValue = computed(() => (model.value !== undefined ? model.value : props.value));
const selectBindings = computed(() => {
  if (resolvedValue.value === undefined) {
    return {};
  }

  return {
    value: resolvedValue.value ?? '',
  };
});
const selectStyle = computed(() => {
  const fill = props.tone === 'dark' ? '%23f5e8ad' : '%235f5f5f';
  return {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M3.25 5.25 7 9l3.75-3.75' stroke='${fill}' stroke-width='1.35' stroke-linecap='square'/%3E%3C/svg%3E")`,
    backgroundPosition: 'right 0.28rem center',
    backgroundSize: '0.68rem 0.68rem',
  };
});

const toneClass = computed(() => {
  switch (props.tone) {
    case 'dark':
      return 'border-conference-line-muted bg-conference-structure text-conference-control-highlight focus:border-conference-accent focus-visible:ring-conference-accent/25';
    case 'chat':
      return 'border-conference-dock-border bg-white text-conference-dock-text focus:border-conference-accent-deep focus-visible:ring-conference-accent/15';
    default:
      return 'border-conference-border bg-white text-conference-ink-soft focus:border-conference-accent-deep focus-visible:ring-conference-accent/15';
  }
});

function handleChange(event: Event): void {
  const nextValue = (event.target as HTMLSelectElement).value;
  model.value = nextValue === '' ? null : nextValue;
}
</script>
